import axiosInstance from 'utils/axiosInterceptors';

const SESSION_FLAG = 'kjauto_push_subscribe_attempted';
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]+$/;

function isValidVapidPublicKey(value: string): boolean {
  const key = value.trim();
  if (!key || key.includes('#{') || key.includes('}#')) {
    return false;
  }

  // VAPID public keys are URL-safe base64 (typically ~87 chars for uncompressed P-256).
  if (key.length < 80 || key.length > 200 || !BASE64URL_PATTERN.test(key)) {
    return false;
  }

  return true;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isWebPushSupported()) {
    return null;
  }

  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (error) {
    console.error('Failed to register service worker', error);
    return null;
  }
}

async function getVapidPublicKey(): Promise<string | null> {
  try {
    const { data } = await axiosInstance.get<{ publicKey: string }>(
      '/api/v1/push/vapid-public-key'
    );
    const publicKey = data?.publicKey?.trim() || null;
    if (!publicKey || !isValidVapidPublicKey(publicKey)) {
      console.warn('Push notifications skipped: VAPID public key is missing or invalid');
      return null;
    }
    return publicKey;
  } catch (error) {
    console.error('Failed to fetch VAPID public key', error);
    return null;
  }
}

async function upsertSubscription(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
    throw new Error('Incomplete push subscription');
  }

  await axiosInstance.post('/api/v1/push/subscriptions', {
    endpoint: json.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    userAgent: navigator.userAgent
  });
}

/**
 * Auto-prompt once per browser session when permission is default;
 * silently re-subscribe when already granted; no-op when denied.
 */
export async function ensurePushSubscription(): Promise<void> {
  try {
    if (!isWebPushSupported()) {
      return;
    }

    if (sessionStorage.getItem(SESSION_FLAG) === '1') {
      return;
    }
    sessionStorage.setItem(SESSION_FLAG, '1');

    if (Notification.permission === 'denied') {
      return;
    }

    const registration = await registerServiceWorker();
    if (!registration) {
      return;
    }

    await navigator.serviceWorker.ready;

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return;
      }
    }

    const publicKey = await getVapidPublicKey();
    if (!publicKey) {
      return;
    }

    let applicationServerKey: Uint8Array;
    try {
      applicationServerKey = urlBase64ToUint8Array(publicKey);
    } catch (error) {
      console.error('Push notifications skipped: invalid VAPID public key encoding', error);
      return;
    }

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
    }

    await upsertSubscription(subscription);
  } catch (error) {
    console.error('Failed to ensure push subscription', error);
  }
}

export async function unsubscribePush(): Promise<void> {
  if (!isWebPushSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    const endpoint = subscription.endpoint;

    try {
      await axiosInstance.delete('/api/v1/push/subscriptions', {
        data: { endpoint }
      });
    } catch (error) {
      console.error('Failed to delete push subscription on server', error);
    }

    await subscription.unsubscribe();
  } catch (error) {
    console.error('Failed to unsubscribe from push', error);
  } finally {
    sessionStorage.removeItem(SESSION_FLAG);
  }
}
